import { useEffect } from 'react'
import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'

const Terms = () => {
  useEffect(() => {
    document.title = 'Terms of Service | BizMatch'
    const meta = document.querySelector('meta[name="description"]')
    if (meta) meta.setAttribute('content', 'Terms of Service for using BizMatch business marketplace.')
  }, [])

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-16 max-w-4xl">
        <div className="prose prose-slate max-w-none">
          <h1 className="text-3xl font-bold text-primary mb-6">Terms of Service – BizMatch</h1>
          
          <p className="text-muted-foreground mb-6">
            <strong>Effective Date:</strong> 24 November 2024
          </p>
          
          <p className="mb-6">
            These Terms of Service ("Terms") govern your use of the BizMatch website, mobile application, and related services ("Services"). By accessing or using BizMatch, you agree to be bound by these Terms. Please read them carefully.
          </p>
          
          <p className="mb-8">
            If you do not agree, you must not use our Services.
          </p>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">1. About Us</h2>
          <p className="mb-3">The Services are operated by BizMatch ("we", "our", "us").</p>
          <p className="mb-3">Contact details:</p>
          <div className="bg-secondary/10 p-6 rounded-lg mb-6">
            <p>Email: Hello@BizMatch.com.au</p>
            <p>Phone: +61 418 616 704</p>
            <p>Address: 2/290 Boundary St, Spring Hill, QLD 4000</p>
          </div>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">2. Eligibility</h2>
          <ul className="list-disc list-inside mb-6 space-y-1">
            <li>You must be at least 18 years old to use BizMatch.</li>
            <li>By registering, you confirm that the information you provide is true, accurate, and current.</li>
            <li>If you create an account on behalf of a business or broker, you represent that you have the authority to do so.</li>
          </ul>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">3. Accounts</h2>
          <ul className="list-disc list-inside mb-6 space-y-1">
            <li>You are responsible for maintaining the confidentiality of your login details.</li>
            <li>You are responsible for all activities under your account.</li>
            <li>Notify us immediately if you believe your account has been compromised.</li>
          </ul>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">4. Services Provided</h2>
          <p className="mb-3">BizMatch provides an online platform where:</p>
          <ul className="list-disc list-inside mb-4 space-y-1">
            <li>Brokers and private sellers ("Sellers") can list businesses for sale.</li>
            <li>Buyers can explore listings and submit enquiries.</li>
            <li>BizMatch actively markets listings and connects interested buyers with Sellers.</li>
          </ul>
          <p className="mb-6">
            We are not a business broker and do not provide financial, legal, or business advice. All negotiations, due diligence, and transactions are strictly between Buyers and Sellers.
          </p>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">5. Listings</h2>
          <ul className="list-disc list-inside mb-6 space-y-1">
            <li>Sellers are solely responsible for the accuracy, legality, and content of their listings.</li>
            <li>Listings must not include false, misleading, or unlawful information.</li>
            <li>We may moderate, remove, or suspend any listing at our discretion.</li>
          </ul>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">6. Fees & Payments</h2>
          <ul className="list-disc list-inside mb-6 space-y-1">
            <li>Private sellers are charged $98 per listing per month, brokers $89 per listing per month.</li>
            <li>A fee of $15-$19 applies for each qualified buyer enquiry ("Lead") generated through the platform, depending on package type.</li>
            <li>Payments are processed securely through third-party providers (e.g., Stripe).</li>
            <li>Fees are non-refundable except as required by law.</li>
          </ul>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">7. Leads & Buyer Enquiries</h2>
          <ul className="list-disc list-inside mb-6 space-y-1">
            <li>Buyer details submitted through "Express Interest" are shared with the relevant Seller.</li>
            <li>Sellers must use buyer information only for responding to the enquiry.</li>
            <li>Unauthorised use, resale, or spamming of buyer data is prohibited.</li>
          </ul>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">8. Acceptable Use</h2>
          <p className="mb-3">You must not use BizMatch to:</p>
          <ul className="list-disc list-inside mb-6 space-y-1">
            <li>Post unlawful, offensive, or fraudulent content.</li>
            <li>Infringe intellectual property or privacy rights.</li>
            <li>Send spam, malicious software, or attempt to breach security.</li>
            <li>Interfere with the Services or other users' experience.</li>
          </ul>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">9. Intellectual Property</h2>
          <ul className="list-disc list-inside mb-6 space-y-1">
            <li>All BizMatch branding, designs, software, and content are owned by or licensed to us.</li>
            <li>You may not copy, modify, or distribute our intellectual property without written consent.</li>
            <li>Sellers retain ownership of their listing content but grant BizMatch a licence to display and promote it on the platform.</li>
          </ul>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">10. Privacy</h2>
          <p className="mb-6">
            Your use of the Services is also governed by our <a href="/privacy" className="text-primary underline hover:text-primary/80">Privacy Policy</a>.
          </p>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">11. Disclaimers</h2>
          <ul className="list-disc list-inside mb-6 space-y-1">
            <li>BizMatch provides the Services on an "as is" basis.</li>
            <li>We do not guarantee that listings will result in enquiries or sales.</li>
            <li>We are not responsible for the conduct of Buyers or Sellers, or the outcome of any transaction.</li>
            <li>We make no warranties about the accuracy, completeness, or reliability of listings.</li>
          </ul>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">12. Limitation of Liability</h2>
          <p className="mb-3">To the extent permitted by law:</p>
          <ul className="list-disc list-inside mb-4 space-y-1">
            <li>We are not liable for any loss, damage, or claims arising from your use of the Services.</li>
            <li>Our total liability to you will not exceed the total fees paid by you to us in the 12 months prior to the claim.</li>
          </ul>
          <p className="mb-6">
            Nothing in these Terms excludes liability for fraud, negligence, or rights that cannot be excluded under Australian Consumer Law.
          </p>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">13. Termination</h2>
          <ul className="list-disc list-inside mb-6 space-y-1">
            <li>You may close your account at any time.</li>
            <li>We may suspend or terminate accounts that breach these Terms.</li>
            <li>Termination does not affect accrued payment obligations.</li>
          </ul>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">14. Changes to the Terms</h2>
          <p className="mb-6">
            We may update these Terms from time to time. The latest version will always be available on our website and app. Continued use of BizMatch after changes means you accept the updated Terms.
          </p>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">15. Governing Law</h2>
          <p className="mb-6">
            These Terms are governed by the laws of Queensland, Australia. Disputes shall be resolved in the courts of Queensland.
          </p>
          
          <h2 className="text-2xl font-semibold text-primary mb-4">16. Contact Us</h2>
          <p className="mb-3">
            If you have any questions about these Terms, please contact:
          </p>
          <div className="bg-secondary/10 p-6 rounded-lg mb-6">
            <p className="font-medium mb-2">BizMatch</p>
            <p>Email: Hello@BizMatch.com.au</p>
            <p>Phone: +61 418 616 704</p>
            <p>Address: 2/290 Boundary St, Spring Hill, QLD 4000</p>
            <p>Website: www.bizmatch.com.au</p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default Terms
